import React from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import Navigation from '@/shared/components/Navigation';
import ToastContainer from '@/shared/components/ToastContainer';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useAdminApp } from '@/modules/admin/context/AdminAppContext';
import AdminTransferAppModal from '@/modules/admin/components/AdminTransferAppModal';
import { GoLiveModal, StatusChangeModal, SendQuoteModal, DeleteAppModal } from '@/modules/admin/components/AdminModals';

const NAV_ITEMS = [
  { path: '', label: 'Overview', icon: '📊' },
  { path: '/charges', label: 'Charges', icon: '💳' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
  { path: '/connectivity', label: 'Connectivity', icon: '🔗' },
  { path: '/dev-guide', label: 'Dev Guide', icon: '📖' },
  { path: '/notes', label: 'Notes', icon: '💬' },
];

export default function AdminAppLayout({ 
  toasts,
  onCloseToast,
  children 
}) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { authFetch } = useAuthContext();
  const { 
    app,
    submitting,
    showGoLiveModal,
    setShowGoLiveModal,
    showStatusChangeModal,
    setShowStatusChangeModal,
    showTransferModal,
    setShowTransferModal,
    showQuoteModal,
    setShowQuoteModal,
    quoteAmount,
    setQuoteAmount,
    quoteCurrency,
    setQuoteCurrency,
    monthlyPrice,
    setMonthlyPrice,
    quoteNote,
    setQuoteNote,
    depositPercentage,
    setDepositPercentage,
    submitQuote,
    closeQuoteModal,
    productionUrl,
    setProductionUrl,
    newStatus,
    setNewStatus,
    handleStatusChangeClick,
    loadApp,
    submitStatusChange,
    submitGoLive,
    showToast,
    // Delete app
    showDeleteModal,
    deleteConfirmText,
    setDeleteConfirmText,
    submitDeleteApp,
    closeDeleteModal,
  } = useAdminApp();
  
  // Base path
  const basePath = `/admin-portal/app/${appId}`;
  const currentPath = location.pathname.replace(basePath, '') || '';
  
  const handleTransferSuccess = async () => {
    await loadApp(appId);
    setShowTransferModal(false);
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <ToastContainer toasts={toasts} onCloseToast={onCloseToast} />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/admin-portal/apps')}
            className="inline-flex items-center text-zapt-purple hover:text-zapt-purple/80 font-medium mb-6"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Apps
          </button>

          {/* Navigation */}
          <div className="bg-white rounded-lg border border-gray-200 mb-6">
            <nav className="flex -mb-px overflow-x-auto" aria-label="Tabs">
                {NAV_ITEMS.map((item) => {
                  const isActive = currentPath === item.path || (item.path === '' && (currentPath === '' || currentPath === '/'));
                  return (
                    <Link
                      key={item.path}
                      to={`${basePath}${item.path}`}
                      className={`
                        flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap
                        ${isActive
                          ? 'border-zapt-purple text-zapt-purple'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <span className="text-lg">{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

          {/* Page Content */}
          <div>
            {children}
          </div>
        </div>
      </div>

      {/* Shared Modals - Available on all pages */}
      <AdminTransferAppModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onSuccess={handleTransferSuccess}
        appId={appId}
        appName={app?.name || ''}
        authFetch={authFetch}
        showToast={showToast}
      />

      <GoLiveModal
        show={showGoLiveModal}
        app={app}
        productionUrl={productionUrl}
        submitting={submitting}
        onClose={() => {
          setShowGoLiveModal(false);
          setProductionUrl('');
        }}
        onSubmit={() => submitGoLive(appId)}
        onProductionUrlChange={setProductionUrl}
      />

      <StatusChangeModal
        show={showStatusChangeModal}
        app={app}
        newStatus={newStatus}
        submitting={submitting}
        onClose={() => {
          setShowStatusChangeModal(false);
          setNewStatus('');
        }}
        onSubmit={() => submitStatusChange(appId)}
      />

      <SendQuoteModal
        show={showQuoteModal}
        app={app}
        activeRequest={app?.quotedAt ? { id: appId } : null}
        quoteAmount={quoteAmount}
        quoteCurrency={quoteCurrency}
        monthlyPrice={monthlyPrice}
        quoteNote={quoteNote}
        depositPercentage={depositPercentage}
        onDepositPercentageChange={setDepositPercentage}
        submitting={submitting}
        onClose={closeQuoteModal}
        onSubmit={() => submitQuote(appId)}
        onAmountChange={setQuoteAmount}
        onCurrencyChange={setQuoteCurrency}
        onMonthlyPriceChange={setMonthlyPrice}
        onNoteChange={setQuoteNote}
      />

      <DeleteAppModal
        show={showDeleteModal}
        app={app}
        confirmText={deleteConfirmText}
        submitting={submitting}
        onClose={closeDeleteModal}
        onSubmit={() => submitDeleteApp(appId)}
        onConfirmTextChange={setDeleteConfirmText}
      />
    </>
  );
}
